import { Component, OnInit, OnDestroy, AfterViewInit, Input, ChangeDetectorRef } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SocialService } from '../../../../../../services/social.service';
import { AuthService } from '../../../../../../core/auth/auth.service';
import { Subject, Subscription, debounceTime, distinctUntilChanged, forkJoin, map, skip } from 'rxjs';
import { LoaderComponent } from '../../../../reusable/loader/loader.component';
import { FleetDetailComponent } from './components/fleet-detail/fleet-detail.component';
import { CreateFleetComponent } from './components/create-fleet/create-fleet.component';
import { ManageMembersComponent } from './components/manage-members/manage-members.component';
import { RestrictiveAcknowledgementDialogComponent } from '../../../../../../shared/components/restrictive-acknowledgement-dialog/restrictive-acknowledgement-dialog.component';
import { TourAnchorDirective } from '../../../../../../features/learning-center/engine/tour-anchor.directive';

import { SubMenuService } from '../../../../../../services/submenu.service';
import { ToastService } from '../../../../../../services/toast.service';


@Component({
  selector: 'app-fleets',
  standalone: true,
  imports: [CommonModule, FormsModule, LoaderComponent, FleetDetailComponent, CreateFleetComponent, ManageMembersComponent, MatMenuModule, MatButtonModule, MatIconModule, RestrictiveAcknowledgementDialogComponent, TourAnchorDirective],

  templateUrl: './fleets.component.html',
  styleUrl: './fleets.component.scss',
})
export class FleetsComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() initialSubTab: string | null = null;
  @Input() initialFleetId: string | null = null;
  private scrollContainer: HTMLElement | null = null;
  private boundScrollHandler!: () => void;
  activeTab = 'find';
  fleets: any[] = [];
  loading = false;
  isProfileOpen = false;

  showFleetOvelay: any

  private currentLoadSub: Subscription | null = null;

  // Search queries
  searchFind = '';
  searchMyFleets = '';
  searchIncoming = '';
  searchSent = '';

  selectedFleet: any = null;
  loadingDetail = false;
  showCreateFleet = false;

  // Material menu state
  selectedFleetForMenu: any = null;
  showManageMembers = false;
  editFleetData: any = null;

  // Leave fleet confirm modal
  showLeaveConfirm = false;
  // Delete fleet confirm modal
  showDeleteConfirm = false;
  // Loading flag for the Leave/Delete confirm dialog buttons.
  confirmActionLoading = false;
  // Restrictive sharing dialog (toggle on/off)
  showFleetRestrictiveDialog = false;
  // Restrictive acknowledgement for join / accept-invite flows
  showJoinRestrictiveDialog = false;
  pendingRestrictiveAction: (() => void) | null = null;


  private searchSubject = new Subject<string>();
  private subscription: Subscription = new Subscription();

  // Pagination
  page = 1;
  pageSize = 10;
  hasMore = true;
  incomingCount = 0;
  sentCount = 0;
  searchLoading = false;

  scrollSentinel: any;

  constructor(
    private socialService: SocialService,
    private authService: AuthService,
    private submenuService: SubMenuService,
    private location: Location,
    private cdr: ChangeDetectorRef,
    public toast: ToastService,
    private router: Router,
  ) { }

  ngOnInit() {
    if (this.initialSubTab) {
      this.activeTab = this.initialSubTab;
    }
    this.fleets = [];
    this.page = 1;
    this.hasMore = true;
    this.loadFleets();

    if (this.initialFleetId) {
      this.viewGroupById(this.initialFleetId);
    }

    this.submenuService.showFleetOverlay$
      .subscribe((show) => {
        this.showFleetOvelay = show;
      });

    // Reset immediately when a new account logs in
    this.subscription.add(
      this.authService.isLoggedIn$.pipe(skip(1)).subscribe((isLoggedIn) => {
        if (isLoggedIn) {
          this.activeTab = 'find';
          this.resetAndLoad();
        }
      })
    );

    // Debounced search
    this.subscription.add(
      this.searchSubject.pipe(
        debounceTime(400),
        distinctUntilChanged()
      ).subscribe(() => {
        this.searchLoading = false;
        if (this.activeTab === 'incoming' || this.activeTab === 'sent') {
          return; // client-side filtering via filteredFleets getter
        }
        this.resetAndLoad();
      })
    );

    // Track profile open state
    this.subscription.add(
      this.submenuService.userProfileOpenState$.subscribe((state: boolean) => {
        this.isProfileOpen = state;
      })
    );
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    if (this.scrollContainer && this.boundScrollHandler) {
      this.scrollContainer.removeEventListener('scroll', this.boundScrollHandler);
    }
  }

  ngAfterViewInit() {
    this.setupScrollListener();
  }

  private setupScrollListener() {
    // The whole profile container scrolls now (header scrolls away, tab row is
    // sticky), so listen on it rather than the old .content-area.
    this.scrollContainer = document.querySelector('.user-profile-container') as HTMLElement | null;
    if (!this.scrollContainer) return;

    this.boundScrollHandler = () => {
      if (!this.hasMore || this.loading) return;
      const el = this.scrollContainer!;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < 300) {
        this.loadFleets();
      }
    };

    this.scrollContainer.addEventListener('scroll', this.boundScrollHandler, { passive: true });
  }

  setActiveTab(tab: string) {
    if (this.activeTab === tab) return;
    this.activeTab = tab;
    this.location.replaceState(`/profile/fleets/${tab}`);
    this.closeDetail();

    // Cancel any in-flight loadFleets request so it can't append after we reset.
    if (this.currentLoadSub) {
      this.currentLoadSub.unsubscribe();
      this.currentLoadSub = null;
    }

    // Clear the in-flight guard BEFORE the single reload. Calling resetAndLoad
    // more than once here (or flipping `loading` between calls) fires two
    // concurrent page-1 requests that both append, duplicating the list.
    this.loading = false;
    this.resetAndLoad();

    const scroller = document.querySelector('.user-profile-container') as HTMLElement;
    if (scroller) scroller.scrollTop = 0;
    const contentArea = document.querySelector('.content-area') as HTMLElement;
    if (contentArea) contentArea.scrollTop = 0;
  }

  clearSearch() {
    this.searchFind = '';
    this.resetAndLoad();
  }

  onSearchInput(value: string) {
    if (this.activeTab === 'incoming' || this.activeTab === 'sent') {
      this.searchLoading = true;
    }
    this.searchSubject.next(value);
  }

  get filteredFleets(): any[] {
    if (this.activeTab === 'incoming' && this.searchIncoming) {
      const q = this.searchIncoming.toLowerCase();
      return this.fleets.filter(f =>
        (f.requester?.name || '').toLowerCase().includes(q) ||
        (f.name || '').toLowerCase().includes(q) ||
        (f.vessel_name || '').toLowerCase().includes(q)
      );
    }
    if (this.activeTab === 'sent' && this.searchSent) {
      const q = this.searchSent.toLowerCase();
      return this.fleets.filter(f =>
        (f.name || '').toLowerCase().includes(q) ||
        (f.subtitle || '').toLowerCase().includes(q) ||
        (f.vessel_name || '').toLowerCase().includes(q)
      );
    }
    return this.fleets;
  }

  resetAndLoad() {
    this.fleets = [];
    this.page = 1;
    this.hasMore = true;
    this.loadFleets();
  }



  loadFleets() {
    if (this.loading || (!this.hasMore && this.page > 1)) return;

    this.loading = true;
    let request;

    // ... (rest of conditions)
    if (this.activeTab === 'find') {
      request = this.socialService.findFleets(this.page, this.pageSize, this.searchFind);
    } else if (this.activeTab === 'my-fleets') {
      request = this.socialService.myFleets(this.page, this.pageSize, this.searchMyFleets);
    } else if (this.activeTab === 'incoming') {
      request = forkJoin({
        invites: this.socialService.getIncomingCommunityInvites(this.page, this.pageSize),
        joinRequests: this.socialService.getJoinRequests(this.page, this.pageSize)
      }).pipe(
        map((res: any) => {
          const invites = res.invites.results || [];
          const joinReqs = res.joinRequests.results || [];
          return {
            results: [...invites, ...joinReqs],
            count: (res.invites.count || 0) + (res.joinRequests.count || 0)
          };
        })
      );
    } else if (this.activeTab === 'sent') {
      request = forkJoin({
        invites: this.socialService.getOutgoingInvites(this.page, this.pageSize),
        joinRequests: this.socialService.getSentFleetRequests(this.page, this.pageSize),

      }).pipe(
        map((res: any) => {
          const invites = res.invites.results || [];
          const joinReqs = res.joinRequests.results || [];
          const combined = [...invites, ...joinReqs].sort(
            (a: any, b: any) => new Date(b.requested_at).getTime() - new Date(a.requested_at).getTime()
          );
          return {
            results: combined,
            count: (res.invites.count || 0) + (res.joinRequests.count || 0)
          };
        })
      );
    }

    if (!request) {
      this.loading = false;
      return;
    }

    this.currentLoadSub = request.subscribe({
      next: (res: any) => {
        let results = res.results || [];

        if (this.activeTab === 'incoming') {
          this.incomingCount = res.count || 0;
          results = results.map((item: any) => {
            const community = item.community || {};
            const requester = item.requester || {};

            return {
              community: community,
              community_id: community._id,
              request_id: item._id,
              request_type: item.request_type,
              requested_at: item.requested_at,
              name: community.name || 'Unknown Fleet',
              image_url: community.image_url,
              community_image_url: community.image_url,
              home_port: community.home_port || null,
              vessel_name: requester.vessel_name || null,
              members_count: community.members_count || 0,
              is_restrictive: community.is_restrictive ?? false,
              requester: {
                name: requester.name,
                image_url: requester.image_url,
              },
              // Use community stats
              activity: community.activity || { routes: 0, fish_caught: 0, places: 0 },
              category: community.category,
            };
          });
        } else if (this.activeTab === 'sent') {
          this.sentCount = res.count || 0;
          results = results.map((item: any) => {
            if (item.request_type === 'community_invite') {
              const target = item.target || {};
              const community = item.community || {};
              return {
                community: community,
                request_id: item._id,
                request_type: item.request_type,
                requested_at: item.requested_at,
                name: target.name || 'Unknown User',
                image_url: target.image_url,
                subtitle: `Invited to ${community.name || 'Fleet'}`,
                vessel_name: target.vessel_name || null,
                mutual_connection_count: target.mutual_connection_count || 0,
                mutual_connections_images: target.mutual_connections_images || target.mutual_connection_images || [],
                activity: target.activity || { routes: 0, fish_caught: 0, places: 0 },
                category: community.category,
                community_id: community._id,
                target_user_id: target._id
              };
            } else {
              // Default for join_request or others
              const community = item.community || {};
              return {
                community: community,
                request_id: item._id,
                request_type: item.request_type,
                requested_at: item.requested_at,
                name: community.name || 'Unknown Fleet',
                image_url: community.image_url,
                subtitle: null,
                home_port: community.home_port || null,
                vessel_name: community.vessel_name || null,
                activity: community.activity || { routes: 0, fish_caught: 0, places: 0 },
                category: community.category,
                members_count: community.members_count || 0,
                mutual_connection_count: community.mutual_connection_count || 0,
                mutual_connections_images: community.mutual_connections_images || community.mutual_connection_images || [],
                community_id: community._id
              };
            }
          });
        }

        if (this.activeTab === 'my-fleets') {
          results = results.map((item: any) => ({ ...item, request_status: 'accepted' }));
        }

        // Defensive de-dupe: never append a fleet/request that's already in the
        // list (guards against overlapping pages or concurrent loads).
        const existingKeys = new Set(
          this.fleets.map((f) => this.getFleetKey(f)).filter(Boolean)
        );
        const uniqueResults = results.filter((item: any) => {
          const key = this.getFleetKey(item);
          if (!key) return true;
          if (existingKeys.has(key)) return false;
          existingKeys.add(key);
          return true;
        });

        this.fleets = [...this.fleets, ...uniqueResults];

        // Fix: handle cursor-based and count-based pagination
        if (res?.next || res?.next_cursor) {
          this.hasMore = true;
        } else if (res?.count !== undefined) {
          this.hasMore = this.fleets.length < res.count;
        } else {
          this.hasMore = results.length >= this.pageSize;
        }
        this.page++;
        this.loading = false;

        // When zoomed out, sentinel may stay intersecting without triggering observer again
        if (this.hasMore) {
          setTimeout(() => this.checkSentinelVisible(), 150);
        }
      },
      error: (err: any) => {
        console.error('Error loading fleets:', err);
        this.loading = false;
      }
    });
  }

  /** Stable identity for a fleet card / request used to de-duplicate the list. */
  private getFleetKey(f: any): string {
    return String(f?._id || f?.id || f?.request_id || f?.community_id || '');
  }

  private checkSentinelVisible() {
    if (!this.scrollSentinel || !this.hasMore || this.loading) return;
    const rect = this.scrollSentinel.nativeElement.getBoundingClientRect();
    if (rect.top < window.innerHeight) {
      this.loadFleets();
    }
  }

  onScroll(_event: any) {
    // Deprecated: replaced by IntersectionObserver
  }


  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.trim().split(' ');
    if (parts.length === 1) {
      return parts[0].substring(0, 2).toUpperCase();
    }
    const first = parts[0][0];
    const second = parts[1][0];
    return (first + second).toUpperCase();
  }

  getSearchPlaceholder(): string {
    switch (this.activeTab) {
      case 'find':
        return 'Search Fleets';
      case 'my-fleets':
        return 'Search My Fleets';
      case 'incoming':
        return 'Search Incoming Requests';
      case 'sent':
        return 'Search Sent Requests';
      default:
        return 'Search Fleets';
    }
  }

  getTimeAgo(dateStr: string): string {
    if (!dateStr) return '';
    // Ensure the date string is treated as UTC if it doesn't have a timezone indicator
    let normalizedDateStr = dateStr;
    if (!normalizedDateStr.endsWith('Z') && !normalizedDateStr.includes('+')) {
      normalizedDateStr = normalizedDateStr + 'Z';
    }
    const date = new Date(normalizedDateStr);
    const now = new Date();
    const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    const days = Math.floor(diffInSeconds / 86400);
    if (days < 7) return `${days}d ago`;
    return `${Math.floor(days / 7)}w ago`;
  }

  acceptRequest(fleet: any, event: any) {
    event.stopPropagation();
    if (fleet.is_restrictive) {
      this.pendingRestrictiveAction = () => this.doAcceptRequest(fleet);
      this.showJoinRestrictiveDialog = true;
      return;
    }
    this.doAcceptRequest(fleet);
  }

  private doAcceptRequest(fleet: any) {
    if (fleet.actionLoading) return;
    // Show the button spinner immediately so the card doesn't vanish before the
    // network call finishes and the user can't double-click.
    fleet.actionLoading = true;
    fleet.loadingAction = 'accept';

    this.socialService.respondToRequest(fleet.request_id, 'accepted').subscribe({
      next: () => {
        // Only remove the card once the server confirms.
        this.fleets = this.fleets.filter(f => f.request_id !== fleet.request_id);
        this.incomingCount = Math.max(0, this.incomingCount - 1);
        this.showSuccess('Request accepted successfully');
      },
      error: (err: any) => {
        console.error(err);
        fleet.actionLoading = false;
        fleet.loadingAction = null;
        this.showError('Error accepting request');
      }
    });
  }

  rejectRequest(fleet: any, event: any) {
    event.stopPropagation();
    if (fleet.actionLoading) return;
    fleet.actionLoading = true;
    fleet.loadingAction = 'reject';

    this.socialService.respondToRequest(fleet.request_id, 'ignored').subscribe({
      next: () => {
        this.fleets = this.fleets.filter(f => f.request_id !== fleet.request_id);
        this.incomingCount = Math.max(0, this.incomingCount - 1);
        this.showSuccess('Request rejected');
      },
      error: (err: any) => {
        console.error(err);
        fleet.actionLoading = false;
        fleet.loadingAction = null;
        this.showError('Error rejecting request');
      }
    });
  }

  cancelRequest(fleet: any) {
    if (fleet.actionLoading) return;
    fleet.actionLoading = true;
    fleet.loadingAction = 'cancel';

    this.socialService.cancelRequest(fleet.request_id).subscribe({
      next: () => {
        this.fleets = this.fleets.filter(f => f.request_id !== fleet.request_id);
        if (this.activeTab === 'sent') this.sentCount = Math.max(0, this.sentCount - 1);
        this.showSuccess('Request cancelled successfully');
      },
      error: (err: any) => {
        console.error(err);
        fleet.actionLoading = false;
        fleet.loadingAction = null;
        this.showError('Error cancelling request');
      }
    });
  }



  requestToJoin(fleet: any) {
    if (fleet.actionLoading || fleet.request_status === 'pending' || fleet.request_status === 'accepted') return;

    if (fleet.is_restrictive) {
      this.pendingRestrictiveAction = () => this.doRequestToJoin(fleet);
      this.showJoinRestrictiveDialog = true;
      return;
    }
    this.doRequestToJoin(fleet);
  }

  private doRequestToJoin(fleet: any) {
    if (fleet.actionLoading) return;
    // Per-card loading so one slow/hung request never blocks the other cards.
    fleet.actionLoading = true;
    this.socialService.sendJoinRequest(fleet._id).subscribe({
      next: () => {
        if (fleet.privacy === 'public') {
          fleet.request_status = 'accepted';
          fleet.my_role = 'member';
          this.showSuccess('Joined fleet');
        } else {
          fleet.request_status = 'pending';
          this.showSuccess('Join request sent');
        }
        fleet.actionLoading = false;
      },
      error: (err: any) => {
        console.error('Error sending join request:', err);
        fleet.actionLoading = false;
        this.showError('Failed to send join request. Please try again.');
      }
    });
  }

  viewGroupForFind(fleet: any) {
    this.submenuService.setFleetOverlay(true);
    this.selectedFleet = fleet;
    const id = fleet._id || fleet.id;
    this.location.replaceState(`/profile/fleets/${this.activeTab}/${id}`);
  }

  viewGroup(post: any) {
    this.submenuService.setFleetOverlay(true);
    const id = post?.community_id;
    if (!id) return;

    this.loadingDetail = true;
    this.selectedFleet = null;
    this.location.replaceState(`/profile/fleets/${this.activeTab}/${id}`);

    this.socialService.getCommunityById(id).subscribe({
      next: (res: any) => {
        const fetched = res.data || res;
        this.selectedFleet = {
          ...fetched,
          request_status: fetched.request_status || post?.community?.request_status || this.activeTab == "incoming" ? "incoming" : "pending",
          my_role: fetched.my_role || post?.community?.my_role || 'member',
        };
        this.loadingDetail = false;
        this.cdr.detectChanges();
      },
      error: (err) => {
        console.error('Error fetching fleet:', err);
        this.selectedFleet = {
          ...post?.community,
          request_status: post?.community?.request_status || 'accepted',
          my_role: post?.community?.my_role || 'member',
        };
        this.loadingDetail = false;
        this.cdr.detectChanges();
      }
    });
  }

  viewGroupById(id: string) {
    this.socialService.getCommunityById(id).subscribe({
      next: (res: any) => {
        const fleet = res.data || res;
        this.selectedFleet = fleet;
      },
      error: (err) => {
        console.error('Error fetching fleet by ID:', err);
      }
    });
  }

  closeDetail() {
    this.selectedFleet = null;
    this.submenuService.setFleetOverlay(false);
    this.location.replaceState(`/profile/fleets/${this.activeTab}`);
  }

  openCreateFleet() {
    this.socialService.checkCanCreateFleet().subscribe({
      next: (res) => {
        if (res.can_create_fleet) {
          this.showCreateFleet = true;
        } else {
          this.showError("You can't create a fleet while in a restrictive fleet.");
        }
      },
      error: () => {
        this.showCreateFleet = true;
      },
    });
  }

  closeCreateFleet() {
    this.showCreateFleet = false;
  }

  // ---------------- SNACKBAR HELPERS ----------------
  private showSuccess(message: string) {
    this.toast.success(message);
  }

  private showError(message: string) {
    this.toast.error(message);
  }

  // ---------------- MENU ACTIONS ----------------

  leaveFleet() {
    if (!this.selectedFleetForMenu) return;
    this.showLeaveConfirm = true;
  }

  confirmLeave() {
    if (!this.selectedFleetForMenu || this.confirmActionLoading) return;
    const fleet = this.selectedFleetForMenu;
    const communityId = fleet._id;
    this.confirmActionLoading = true;

    this.socialService.leaveCommunity(communityId).subscribe({
      next: () => {
        this.confirmActionLoading = false;
        this.showLeaveConfirm = false;
        this.fleets = this.fleets.filter(f => f._id !== communityId);
        this.showSuccess('Successfully left the fleet');
      },
      error: (err: any) => {
        console.error('Error leaving fleet:', err);
        this.confirmActionLoading = false;
        this.showError('Failed to leave fleet. Please try again.');
      }
    });
  }

  editFleetMenu() {
    if (this.selectedFleetForMenu) {
      this.editFleetData = this.selectedFleetForMenu;
      this.showCreateFleet = true;
    }
  }

  manageMembersMenu() {
    if (this.selectedFleetForMenu) {
      this.showManageMembers = true;
    }
  }

  deleteFleet() {
    if (!this.selectedFleetForMenu) return;
    this.showDeleteConfirm = true;
  }

  confirmDelete() {
    if (!this.selectedFleetForMenu || this.confirmActionLoading) return;
    const communityId = this.selectedFleetForMenu._id;
    this.confirmActionLoading = true;

    this.socialService.deleteCommunity(communityId).subscribe({
      next: () => {
        this.confirmActionLoading = false;
        this.showDeleteConfirm = false;
        this.fleets = this.fleets.filter(f => f._id !== communityId);
        this.showSuccess('Fleet deleted successfully');
      },
      error: (err: any) => {
        console.error('Error deleting fleet:', err);
        this.confirmActionLoading = false;
        this.showError('Failed to delete fleet. Please try again.');
      }
    });
  }

  fleetGeofenceMenu() {
    // Placeholder for geofence functionality
    console.log('Geofence menu clicked for:', this.selectedFleetForMenu?.name);
  }

  toggleRestrictiveMenu() {
    if (!this.selectedFleetForMenu) return;
    if (this.selectedFleetForMenu.is_restrictive) {
      // Turn OFF directly
      const formData = new FormData();
      formData.append('is_restrictive', 'false');
      this.socialService.updateFleet(this.selectedFleetForMenu._id, formData).subscribe({
        next: () => {
          this.selectedFleetForMenu.is_restrictive = false;
          const idx = this.fleets.findIndex(f => f._id === this.selectedFleetForMenu._id);
          if (idx !== -1) this.fleets[idx].is_restrictive = false;
          this.showSuccess('Restrictive sharing disabled');
        },
        error: () => this.showError('Failed to update restrictive sharing'),
      });
    } else {
      // Turn ON — show acknowledgement first
      this.showFleetRestrictiveDialog = true;
    }
  }

  onFleetRestrictiveAgreed() {
    this.showFleetRestrictiveDialog = false;
    if (!this.selectedFleetForMenu) return;
    const formData = new FormData();
    formData.append('is_restrictive', 'true');
    this.socialService.updateFleet(this.selectedFleetForMenu._id, formData).subscribe({
      next: () => {
        this.selectedFleetForMenu.is_restrictive = true;
        const idx = this.fleets.findIndex(f => f._id === this.selectedFleetForMenu._id);
        if (idx !== -1) this.fleets[idx].is_restrictive = true;
        this.showSuccess('Restrictive sharing enabled');
      },
      error: () => this.showError('Failed to update restrictive sharing'),
    });
  }

  onFleetRestrictiveDisagreed() {
    this.showFleetRestrictiveDialog = false;
  }

  onJoinRestrictiveAgreed() {
    this.showJoinRestrictiveDialog = false;
    this.pendingRestrictiveAction?.();
    this.pendingRestrictiveAction = null;
  }

  onJoinRestrictiveDisagreed() {
    this.showJoinRestrictiveDialog = false;
    this.pendingRestrictiveAction = null;
  }

  closeManageMembers() {
    this.showManageMembers = false;
  }

  /** Only an owner/admin of the fleet whose roster is open may manage it. */
  get canManageSelectedFleet(): boolean {
    const role = String(this.selectedFleetForMenu?.my_role || '').toLowerCase();
    return role === 'owner' || role === 'admin';
  }

  /**
   * A member row in the roster was tapped — open that captain's full profile.
   * Available to every role: browsing the roster is not an admin privilege.
   */
  onMemberSelected(member: any): void {
    const userId = String(member?.userId || '').trim();
    if (!userId) return;
    this.closeManageMembers();
    this.router.navigate(['/dockmates', userId, 'full-profile']);
  }

  onFleetSaved(updatedFleet: any) {
    // Update local list if editing
    if (this.editFleetData) {
      const index = this.fleets.findIndex(f => f._id === updatedFleet._id);
      if (index !== -1) {
        this.fleets[index] = { ...this.fleets[index], ...updatedFleet };
      }
      this.editFleetData = null;
    } else {
      // If was creating new
      this.resetAndLoad();
    }
  }
}
